import React, { useState, useEffect } from 'react'
import Button from '@/components/ui/Button'
import Tag from '@/components/ui/Tag'
import SidePanel from '@/components/ui/SidePanel'
import MemoModal from '@/components/ui/MemoModal'
import RequiredLabel from '@/components/ui/RequiredLabel'
import { designTokens } from '@/utils/design-tokens'
import { handleApiError } from '@/utils/api-error-handler'
import { useAlert } from '@/components/ui/Alert/exports'
import { TaskSubgroupService } from '@/api/generated/services/TaskSubgroupService'
import { TaskSubGroupCreateRequest } from '@/api/generated/models/TaskSubGroupCreateRequest'
import { TaskSubGroupUpdateRequest } from '@/api/generated/models/TaskSubGroupUpdateRequest'

interface TaskSubgroupCreatePanelProps {
  isOpen: boolean
  onClose: () => void
  taskGroupId: string
  taskGroupName: string
  businessName: string
  onSuccess?: (createdId?: string) => void
  // 수정 모드를 위한 추가 props
  editMode?: boolean
  taskSubgroupId?: string
  initialTaskSubgroupName?: string
}

// Section Header Component
const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <div 
    className="pb-2 border-b" 
    style={{ 
      borderBottomColor: '#EBEBEB',
      borderBottomWidth: '1px',
      padding: '7px 0px'
    }}
  >
    <h3 
      style={{
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: '18px',
        fontWeight: 700,
        color: designTokens.colors.gray[6]
      }}
    >
      {title}
    </h3>
  </div>
)

// Info Field Component
const InfoField: React.FC<{ 
  label: string
  value: string | React.ReactNode
  fullWidth?: boolean 
}> = ({ label, value, fullWidth = false }) => (
  <div className={`flex ${fullWidth ? 'flex-col gap-2' : 'items-center gap-5'}`}>
    <div className={fullWidth ? '' : 'w-37 flex-shrink-0'}>
      <span 
        style={{
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: '16px',
          fontWeight: 400,
          color: designTokens.colors.gray[6]
        }}
      >
        {label}
      </span>
    </div>
    <div className={fullWidth ? '' : 'flex-1 py-2'}>
      {typeof value === 'string' ? (
        <span 
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '16px',
            fontWeight: 400,
            color: designTokens.colors.gray[6]
          }}
        >
          {value}
        </span>
      ) : (
        value
      )}
    </div>
  </div>
)

const TaskSubgroupCreatePanel: React.FC<TaskSubgroupCreatePanelProps> = ({
  isOpen,
  onClose,
  taskGroupId,
  taskGroupName,
  businessName,
  onSuccess,
  editMode = false,
  taskSubgroupId,
  initialTaskSubgroupName = ''
}) => {
  const [taskSubgroupName, setTaskSubgroupName] = useState(initialTaskSubgroupName)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isMemoModalOpen, setIsMemoModalOpen] = useState(false)
  const { showError, showSuccess } = useAlert()

  // 수정 모드일 때 초기값 설정
  useEffect(() => {
    if (editMode && initialTaskSubgroupName) {
      setTaskSubgroupName(initialTaskSubgroupName)
    }
  }, [editMode, initialTaskSubgroupName])

  const handleCancel = () => {
    setTaskSubgroupName('')
    onClose()
  }

  const handleSave = async () => {
    if (!taskSubgroupName.trim()) {
      showError('실행과제명을 입력해주세요.')
      return
    }

    if (!editMode && !taskGroupId) {
      showError('전략과제가 선택되지 않았습니다.')
      return
    }

    // 수정 모드: 메모 입력 모달을 띄운 뒤 저장
    if (editMode && taskSubgroupId) {
      setIsMemoModalOpen(true)
      return
    }

    // 생성 모드: Create API 호출
    setIsSubmitting(true)
    try {
      const createRequest: TaskSubGroupCreateRequest = {
        task_group_id: taskGroupId,
        name: taskSubgroupName.trim()
      }

      const response = await TaskSubgroupService.appsTaskViewsCreateTaskSubgroup({
        requestBody: createRequest
      })

      // Success - reset form and close panel
      setTaskSubgroupName('')
      onClose()
      showSuccess('실행과제가 생성되었습니다.')
      onSuccess?.(response?.id)
    } catch (err) {
      console.error('Failed to create task subgroup:', err)
      handleApiError(err, showError, '실행과제 생성에 실패했습니다')
    } finally {
      setIsSubmitting(false)
    }
  }

  // 수정 저장 확정 (메모 모달 확인 시): memo 포함하여 Update API 호출
  const handleUpdateConfirm = async (memo?: string) => {
    if (!taskSubgroupId) return

    setIsSubmitting(true)
    try {
      const updateRequest: TaskSubGroupUpdateRequest = {
        name: taskSubgroupName.trim(),
        memo
      }

      await TaskSubgroupService.appsTaskViewsUpdateTaskSubgroup({
        subGroupId: taskSubgroupId,
        requestBody: updateRequest
      })

      setIsMemoModalOpen(false)
      setTaskSubgroupName('')
      onClose()
      showSuccess('실행과제가 수정되었습니다.')
      onSuccess?.(taskSubgroupId)
    } catch (err) {
      console.error('Failed to update task subgroup:', err)
      handleApiError(err, showError, '실행과제 수정에 실패했습니다')
    } finally {
      setIsSubmitting(false)
    }
  }

  const headerActions = (
    <>
      <Button
        size="small"
        color="white"
        onClick={handleCancel}
        disabled={isSubmitting}
        className="flex items-center gap-1"
      >
        취소
      </Button>
      <Button
        size="small"
        color="dark"
        onClick={handleSave}
        disabled={isSubmitting || !taskSubgroupName.trim()}
        className="flex items-center gap-1"
      >
        {isSubmitting ? '저장 중...' : '저장'}
      </Button>
    </>
  )

  return (
    <>
    <SidePanel
      isOpen={isOpen}
      onClose={handleCancel}
      title={editMode ? "실행과제 수정" : "실행과제 생성"}
      width={800}
      headerActions={headerActions}
    >
      <div 
        className="flex flex-col space-y-2" 
        style={{ 
          padding: '24px',
        }}
      >
        {/* Section: 기본정보 */}
        <div className="space-y-2">
          <SectionHeader title="기본정보" />
          <div className="space-y-2" style={{ padding: '0px 4px' }}>
            <InfoField 
              label="유형" 
              value={
                <Tag color="warning" size="small">
                  ● 실행과제
                </Tag>
              }
            />
            <InfoField 
              label="계층" 
              value={`${businessName} > ${taskGroupName}`}
            />
            <InfoField
              label={<RequiredLabel>실행과제명</RequiredLabel>}
              value={
                <input
                  type="text"
                  value={taskSubgroupName}
                  onChange={(e) => setTaskSubgroupName(e.target.value)}
                  placeholder="실행과제명을 입력하세요"
                  disabled={isSubmitting}
                  className="w-full p-3 border rounded"
                  style={{
                    borderColor: designTokens.colors.gray[1],
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: '16px',
                    color: designTokens.colors.gray[6]
                  }}
                />
              }
              fullWidth
            />
          </div>
        </div>
      </div>
    </SidePanel>

    <MemoModal
      isOpen={isMemoModalOpen}
      onClose={() => setIsMemoModalOpen(false)}
      onConfirm={handleUpdateConfirm}
      title="실행과제 수정"
      isSubmitting={isSubmitting}
    />
    </>
  )
}

export default TaskSubgroupCreatePanel